import type {
    CreatureSpeedSource,
    CreatureSpeedTokenReferenceInput,
    CreatureSpeedTokenReferenceResult,
    CreatureWalkingSpeedFoldResult,
} from '../types';
import { applyCreatureSpeedFactorRows } from './creature-speed-factor';

/** Apply one second-pass [speed_*] token upgrade to an already-computed speed. */
export function upgradeCreatureSpeedTokenReference(input: CreatureSpeedTokenReferenceInput): CreatureSpeedTokenReferenceResult {
    if (input.tokenBase <= 0) return { changed: false, total: input.currentTotal, persisted: null };

    const dynamicSources: CreatureSpeedSource[] = input.dynamicSources.map(source => ({ ...source }));
    const inventoryIndexes = dynamicSources
        .map((source, index) => source.kind === 'inventory_equipped' ? index : -1)
        .filter(index => index >= 0);
    if (inventoryIndexes.length > 1) {
        let winnerIndex = inventoryIndexes[0] ?? 0;
        for (const index of inventoryIndexes.slice(1)) {
            if ((dynamicSources[index]?.value ?? 0) > (dynamicSources[winnerIndex]?.value ?? 0)) winnerIndex = index;
        }
        for (const index of inventoryIndexes) {
            if (index === winnerIndex) continue;
            const source = dynamicSources[index];
            if (source) dynamicSources[index] = { ...source, raw_value: source.value, value: 0, suppressed: true };
        }
    }
    if (input.dynamicBrace !== null) return { changed: false, total: input.currentTotal, persisted: null };

    const dynamic = dynamicSources.reduce((sum, source) => sum + source.value, 0);
    const newTotal = applyCreatureSpeedFactorRows(dynamicSources, input.tokenBase + dynamic, false).total;
    if (newTotal <= input.currentTotal) return { changed: false, total: input.currentTotal, persisted: null };

    const derivedSource: CreatureSpeedSource = {
        category: 'derived',
        label: input.tokenLabel !== '' ? input.tokenLabel : `${input.speedName.charAt(0).toUpperCase()}${input.speedName.slice(1)} (derived)`,
        value: input.tokenBase,
        href_id: input.tokenHrefId,
        kind: 'derived',
    };
    const sources = [derivedSource, ...dynamicSources];
    const categoryTotals = new Map<string, number>();
    for (const source of sources) {
        const category = typeof source.category === 'string' ? source.category : source.kind;
        categoryTotals.set(category, (categoryTotals.get(category) ?? 0) + source.value);
    }
    const breakdown = Object.fromEntries(categoryTotals);
    const persisted: CreatureWalkingSpeedFoldResult = {
        total: newTotal,
        base: 0,
        locked: false,
        sources,
        breakdown,
    };
    return { changed: true, total: newTotal, persisted };
}
